import { useEffect, useMemo } from "react";
import { useQuery } from "urql";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import type { TournamentPhase } from "@/gen/graphql";
import { layoutTemplatesQuery } from "@/routes/tournament-layout-templates/queries/layoutTemplates";

interface LayoutTemplateSelectorProps {
    phase: TournamentPhase;
    value: string | null | undefined;
    onChange: (value: string | null) => void;
    className?: string;
}

export function LayoutTemplateSelector({
    phase,
    value,
    onChange,
    className,
}: LayoutTemplateSelectorProps) {
    const [{ data, fetching }] = useQuery({
        query: layoutTemplatesQuery,
        variables: { phase },
    });

    const templates = useMemo(() => data?.tournamentLayoutTemplates || [], [data]);

    // Auto-select default template if no value is set
    useEffect(() => {
        if (!value && templates.length > 0 && !fetching) {
            const defaultTemplate = templates.find(t => t.isDefault);
            if (defaultTemplate) {
                onChange(defaultTemplate.id);
            }
        }
    }, [templates, value, onChange, fetching]);

    const phaseName = phase === "BEFORE" ? "Before" : phase === "DURING" ? "During" : "After";

    return (
        <Select value={value || undefined} onValueChange={onChange} disabled={fetching}>
            <SelectTrigger className={className}>
                <SelectValue placeholder={`Select ${phaseName.toLowerCase()} template`} />
            </SelectTrigger>
            <SelectContent>
                {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                        {template.name}
                        {template.isDefault && " (Default)"}
                    </SelectItem>
                ))}
                {templates.length === 0 && !fetching && (
                    <div className="px-2 py-1 text-sm text-gray-500">
                        No templates available for {phaseName.toLowerCase()} phase
                    </div>
                )}
            </SelectContent>
        </Select>
    );
}
